import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import {
  type AudioRecordingSubmission,
  abandonAudioRecordingSubmission,
  beginAudioRecordingDraft,
  prepareAudioRecordingFile,
  prepareAudioRecordingSubmission,
  readAudioRecordingSubmission,
  runAudioRecordingUpload,
} from "@/lib/audio-recording-submission";
import { refreshOwnUsage } from "@/lib/usage-queries";
import {
  getVisitScope,
  refreshVisitReads,
  useVisitScope,
} from "@/lib/visit-queries";
import { usePostOnboardingStore } from "@/store/postOnboarding.store";

const message = (error: unknown) =>
  error instanceof Error
    ? error.message
    : t("visit.recovery.recordingUncertain");
export const useAudioRecordingSubmit = ({
  visitPublicId,
  open,
}: {
  visitPublicId: string | undefined;
  open: boolean;
}) => {
  const readScope = useVisitScope();
  const account = readScope
    ? `${readScope.uid}:${readScope.accountPublicId}`
    : null;
  const scope = `${account}:${readScope?.generation}:${visitPublicId}:${open}`;
  const active = useRef<{ scope: string } | null>(null);
  if (active.current?.scope !== scope) active.current = { scope };
  const lifetime = active.current;
  const controller = useRef<AbortController | null>(null);
  const [, refresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    active.current = lifetime;
    setLoading(false);
    setError(null);
    return () => {
      if (active.current === lifetime) active.current = null;
      controller.current?.abort();
      controller.current = null;
    };
  }, [lifetime]);
  const current = () =>
    open && active.current === lifetime && getVisitScope() === readScope;
  let state: AudioRecordingSubmission | null = null;
  let storageError: string | null = null;
  if (account && visitPublicId) {
    try {
      state = readAudioRecordingSubmission(account, visitPublicId);
    } catch (failure) {
      storageError = message(failure);
    }
  }
  const submit = async (data?: {
    file: Blob;
    duration: number;
    language: string;
    // The recording's live transcript: kept as is, with the audio.
    transcription?: string;
    recordingConsent?: true;
  }): Promise<boolean> => {
    if (controller.current) return false;
    const operation = new AbortController();
    controller.current = operation;
    setLoading(true);
    setError(null);
    try {
      if (!account || !visitPublicId || !readScope || !current())
        throw Error("Sign in and select a visit before submitting.");
      const previous = readAudioRecordingSubmission(account, visitPublicId);
      const prepared = data
        ? await prepareAudioRecordingFile({
            file: data.file,
            settings: {
              duration: data.duration,
              language: data.language,
              ...(data.transcription === undefined
                ? {}
                : { transcription: data.transcription }),
              ...(data.recordingConsent
                ? { recordingConsent: data.recordingConsent }
                : {}),
            },
            signal: operation.signal,
          })
        : undefined;
      operation.signal.throwIfAborted();
      if (!current()) return false;
      const pending = prepared
        ? prepareAudioRecordingSubmission(account, visitPublicId, prepared)
        : previous;
      if (!pending) throw Error("Select an audio file before submitting.");
      await runAudioRecordingUpload({
        account,
        parent: visitPublicId,
        recordingKey: pending.recordingKey,
        prepared,
        userUid: readScope.uid,
        signal: operation.signal,
        isCurrent: current,
      });
      if (!current()) return false;
      void refreshVisitReads(readScope);
      void refreshOwnUsage(readScope);
      usePostOnboardingStore.getState().markDone();
      return true;
    } catch (failure) {
      if (current()) setError(message(failure));
      return false;
    } finally {
      if (controller.current === operation) controller.current = null;
      if (current()) {
        setLoading(false);
        refresh((value) => value + 1);
      }
    }
  };
  const beginDraft = (): boolean => {
    try {
      if (!account || !visitPublicId || !current() || controller.current)
        throw Error("Sign in and select a visit before submitting.");
      beginAudioRecordingDraft(account, visitPublicId);
      setError(null);
      refresh((value) => value + 1);
      return true;
    } catch (failure) {
      setError(message(failure));
      return false;
    }
  };
  const abandon = (): boolean => {
    try {
      if (!account || !visitPublicId || !current() || controller.current)
        return false;
      abandonAudioRecordingSubmission(account, visitPublicId);
      setError(null);
      refresh((value) => value + 1);
      return true;
    } catch (failure) {
      setError(message(failure));
      return false;
    }
  };
  return {
    submit,
    beginDraft,
    abandon,
    loading,
    error: error ?? storageError,
    recovery:
      storageError !== null || (state !== null && state.kind !== "accepted"),
    canRetry: state?.kind === "accepting" || state?.kind === "accepted",
  };
};
